// selectors
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { createDealRootSelector } from '../../offerCompareColumn/hooks/createDealRootSelector';
import { useCtPropertyTaxItems } from '../../offerCompareColumn/hooks/useCtPropertyTaxItems';

// components
import DealSummaryItem from '../common/DealSummaryItem';
import { Grid } from '@interstate/components/Grid';
import { formatDollarsAndCents, formatNumber } from '@makemydeal/dr-common-utils';
import LineItemWithChildren from '../common/LineItemWithChildren';

// constants
import * as constants from '../../constants';

// types
import type { DealHistory } from '@makemydeal/dr-dash-types';

// styles
import {
    labelStyles as defaultLabelStyles,
    expandibleButtonStyles,
    expandibleAreaStyles,
    valueStyles as defaultValueStyles,
    secondaryValueStyles,
    detailedViewLabelStyles,
    detailedViewValueStyles,
    detailedViewGridProps,
    defaultGridProps
} from '../utils/styles';

import { useDealSummary } from '../../common/DealSummaryProvider';
import { useMediaQuery } from 'react-responsive';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import { useHeldValue } from '../../hooks/useHeldSelector';

type InterstatePaymentProps = {
    deal?: DealHistory;
};

const Payment = ({ deal }: InterstatePaymentProps) => {
    const useDealRootSelector = createDealRootSelector(deal, 'Payment');
    const monthlyPayment = useHeldValue(useDealRootSelector(offerSelectors.getMonthlyPayment, 'monthlyPayment'), deal);
    const baseMonthlyPayment = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getBaseMonthlyPayment, 'baseMonthlyPayment'),
        deal
    );
    const depreciation = useHeldValue(useDealRootSelector(offerReduxSelectors.getTotalDepreciation, 'totalDepreciation'), deal);
    const rentCharge = useHeldValue(useDealRootSelector(offerReduxSelectors.getTotalInterest, 'totalInterest'), deal);
    const monthlyUseTax = useHeldValue(useDealRootSelector(offerReduxSelectors.getMonthlyUseTax, 'monthlyUseTax'), deal);
    const ctPropertyTaxItems = useCtPropertyTaxItems(deal);
    const selectedTermMonths = useDealRootSelector(offerSelectors.getSelectedTermMonths, 'selectedTermMonths');
    const heldTermMonths = useHeldValue(selectedTermMonths, deal);
    const term = selectedTermMonths ?? heldTermMonths;
    const totalBasePayment = useHeldValue(
        useDealRootSelector(offerReduxSelectors.getTotalOfBasePayments, 'totalOfBasePayments'),
        deal
    );
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const { showDetailedView } = useDealSummary();
    const labelStyles = showDetailedView ? detailedViewLabelStyles : defaultLabelStyles;
    const valueStyles = showDetailedView ? detailedViewValueStyles : defaultValueStyles;
    const gridProps = showDetailedView && isLargeSize ? detailedViewGridProps : defaultGridProps;

    return (
        <DealSummaryItem
            labelStyles={!showDetailedView ? labelStyles : {}}
            valueStyles={valueStyles}
            label={constants.PAYMENT}
            value={formatDollarsAndCents(monthlyPayment)}
        >
            <Grid container sx={gridProps}>
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={labelStyles}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.TOTAL_BASE_PAYMENT}
                    value={formatDollarsAndCents(totalBasePayment)}
                >
                    <LineItemWithChildren
                        containerStyles={{
                            pr: 0
                        }}
                        label={constants.PAYMENT_DEPRECIATION}
                        value={formatDollarsAndCents(depreciation)}
                    />
                    <LineItemWithChildren
                        containerStyles={{
                            pr: 0
                        }}
                        label={constants.PAYMENT_RENT_CHARGE}
                        value={formatDollarsAndCents(rentCharge)}
                    />
                </DealSummaryItem>
                <LineItemWithChildren
                    containerStyles={{ py: 1 }}
                    label={constants.TERM}
                    value={formatNumber(term ?? 0)}
                    labelStyles={showDetailedView ? detailedViewLabelStyles : {}}
                    valueStyles={showDetailedView ? secondaryValueStyles : {}}
                />
                <DealSummaryItem
                    valueStyles={secondaryValueStyles}
                    labelStyles={labelStyles}
                    expandibleButtonStyles={expandibleButtonStyles}
                    expandibleAreaStyles={expandibleAreaStyles}
                    label={constants.MONTHLY_PAYMENT}
                    value={formatDollarsAndCents(monthlyPayment)}
                >
                    <LineItemWithChildren label={constants.BASE_MO_PAYMENT} value={formatDollarsAndCents(baseMonthlyPayment)} />
                    {ctPropertyTaxItems.map((taxItem) => (
                        <LineItemWithChildren
                            key={taxItem.taxName}
                            label={taxItem.taxDescription}
                            value={formatDollarsAndCents(taxItem.monthlyTax)}
                        />
                    ))}
                    <LineItemWithChildren label={constants.MO_USE_TAX} value={formatDollarsAndCents(monthlyUseTax)} />
                </DealSummaryItem>
            </Grid>
        </DealSummaryItem>
    );
};

export default Payment;
